import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Truck, MapPin, AlertTriangle, Battery, Clock, Navigation,
  RefreshCw, Phone, Share2, Eye, Wifi, WifiOff, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FleetDashboardPage() {
  const [couriers, setCouriers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [hospitalLink, setHospitalLink] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const wsRef = useRef(null);

  const token = localStorage.getItem('sterilemate_token');

  // Initialize Leaflet map
  useEffect(() => {
    // Only initialize map after loading is complete and mapRef is available
    if (loading || !mapRef.current || mapInstanceRef.current) return;

    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      // Initialize map centered on Houston, TX
      const L = window.L;
      const map = L.map(mapRef.current).setView([29.7604, -95.3698], 11);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      mapInstanceRef.current = map;
    };
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [loading]);

  // Fetch fleet status
  const fetchFleetStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fleet/couriers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCouriers(data.couriers || []);
      updateMapMarkers(data.couriers || []);
    } catch (err) {
      console.error('Error fetching fleet:', err);
    }
  }, [token]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fleet/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAlerts(data.alerts || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  }, [token]);

  // Update map markers
  const updateMapMarkers = useCallback((courierList) => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;

    courierList.forEach(courier => {
      const existingMarker = markersRef.current[courier.courier_id];
      const position = [courier.lat, courier.lon];

      // Create custom icon based on status
      const iconColor = courier.status === 'moving' ? 'green' : 
                       courier.status === 'stopped' ? 'orange' : 'red';
      
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            width: 36px; height: 36px; 
            background: ${iconColor}; 
            border-radius: 50%; 
            border: 3px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex; align-items: center; justify-content: center;
          ">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M18 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5m1.5-9H17V12h4.46L19.5 9.5M6 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5M20 8l3 4v5h-2c0 1.66-1.34 3-3 3s-3-1.34-3-3H9c0 1.66-1.34 3-3 3s-3-1.34-3-3H1V6c0-1.11.89-2 2-2h14v4h3M3 6v9h.76c.55-.61 1.35-1 2.24-1s1.69.39 2.24 1H15V6H3z"/>
            </svg>
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
      });

      if (existingMarker) {
        existingMarker.setLatLng(position);
        existingMarker.setIcon(icon);
      } else {
        const marker = L.marker(position, { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width: 150px">
              <strong>${courier.courier_name}</strong><br/>
              ${courier.status === 'moving' ? '🟢 Moving' : '🟠 Stopped'}<br/>
              ${courier.eta_minutes ? `ETA: ${courier.eta_minutes} min` : ''}<br/>
              ${courier.job_id ? `Job: ${courier.job_id}` : 'No active job'}
            </div>
          `);
        markersRef.current[courier.courier_id] = marker;
      }
    });
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/fleet/ws/fleet`);

    ws.onopen = () => {
      setWsConnected(true);
      console.log('Fleet WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'initial_state') {
          setCouriers(message.couriers || []);
          updateMapMarkers(message.couriers || []);
        } else if (message.type === 'courier_move') {
          setCouriers(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(c => c.courier_id === message.data.courier_id);
            if (idx >= 0) {
              updated[idx] = message.data;
            } else {
              updated.push(message.data);
            }
            return updated;
          });
          updateMapMarkers([message.data]);
        }
      } catch (err) {
        console.error('WebSocket message error:', err);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      console.log('Fleet WebSocket disconnected');
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };

    wsRef.current = ws;

    // Ping to keep alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [token, updateMapMarkers]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFleetStatus(), fetchAlerts()]);
      setLoading(false);
    };
    loadData();

    // Refresh alerts every 30 seconds
    const alertInterval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(alertInterval);
  }, [fetchFleetStatus, fetchAlerts]);

  // Create hospital view link
  const createHospitalLink = async (jobId) => {
    try {
      const res = await fetch(`${API}/api/fleet/hospital-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ job_id: jobId, expires_minutes: 120 })
      });
      const data = await res.json();
      setHospitalLink(data);
    } catch (err) {
      console.error('Error creating link:', err);
    }
  };

  // Focus map on courier
  const focusOnCourier = (courier) => {
    setSelectedCourier(courier);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([courier.lat, courier.lon], 15);
      markersRef.current[courier.courier_id]?.openPopup();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'moving': return 'bg-green-500';
      case 'stopped': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 border-red-300 text-red-800';
      case 'medium': return 'bg-orange-100 border-orange-300 text-orange-800';
      default: return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="fleet-dashboard">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Fleet Command Center</h1>
              <p className="text-sm text-gray-400">Real-time courier tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${wsConnected ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
              {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              {wsConnected ? 'Live' : 'Disconnected'}
            </div>
            <Button variant="outline" size="sm" onClick={fetchFleetStatus}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Sidebar - Courier List */}
        <div className="w-80 bg-gray-800 border-r border-gray-700 overflow-y-auto">
          {/* Alerts Section */}
          {alerts.length > 0 && (
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Alerts ({alerts.length})
              </h3>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert, i) => (
                  <div 
                    key={i} 
                    className={`p-2 rounded border ${getAlertSeverityColor(alert.severity)}`}
                  >
                    <p className="text-xs font-medium">{alert.courier_name}</p>
                    <p className="text-xs">{alert.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Couriers */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              Active Couriers ({couriers.length})
            </h3>
            <div className="space-y-2">
              {couriers.map(courier => (
                <div 
                  key={courier.courier_id}
                  className={`p-3 rounded-lg bg-gray-700/50 hover:bg-gray-700 cursor-pointer transition-colors ${selectedCourier?.courier_id === courier.courier_id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => focusOnCourier(courier)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(courier.status)}`} />
                      <span className="font-medium text-sm">{courier.courier_name}</span>
                    </div>
                    {courier.battery && (
                      <div className={`flex items-center gap-1 text-xs ${courier.battery < 20 ? 'text-red-400' : 'text-gray-400'}`}>
                        <Battery className="w-3 h-3" />
                        {courier.battery}%
                      </div>
                    )}
                  </div>
                  
                  {courier.job_id && (
                    <div className="text-xs text-gray-400 mb-1">
                      Job: {courier.job_id}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {courier.eta_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        ETA: {courier.eta_minutes}m
                      </span>
                    )}
                    {courier.speed > 0 && (
                      <span className="flex items-center gap-1">
                        <Navigation className="w-3 h-3" />
                        {Math.round(courier.speed)} km/h
                      </span>
                    )}
                  </div>

                  {courier.job_id && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="w-full mt-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        createHospitalLink(courier.job_id);
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Hospital Link
                    </Button>
                  )}
                </div>
              ))}

              {couriers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No active couriers</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-full" />
          
          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-gray-800/90 backdrop-blur rounded-lg p-3 text-xs">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500" /> Moving
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-orange-500" /> Stopped
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500" /> Offline
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hospital Link Modal */}
      {hospitalLink && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-gray-800 text-white border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" /> Hospital Tracking Link
              </CardTitle>
              <CardDescription className="text-gray-400">
                Share this link with hospital staff to track the delivery
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400">Tracking URL</label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      value={hospitalLink.tracking_url}
                      readOnly
                      className="bg-gray-700 border-gray-600"
                    />
                    <Button 
                      onClick={() => {
                        navigator.clipboard.writeText(hospitalLink.tracking_url);
                        alert('Link copied!');
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  <p>Job ID: {hospitalLink.job_id}</p>
                  <p>Expires: {new Date(hospitalLink.expires_at).toLocaleString()}</p>
                </div>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => setHospitalLink(null)}
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
